/**
 * Pure helpers for the "Load .env.example from image" prefill flow in the
 * runtime tab (issue #77). Kept out of the component so the routing rules are
 * unit-testable.
 *
 * Key rule (#77): a SECRET-hinted entry is scaffolded into the **Secret values**
 * field (`env_secrets_text`, as `KEY=`) — the single encrypted secret path
 * (#69/ADR-019). A plain entry goes into `env_text` with its default. An
 * already-configured key is left untouched (no duplicate, no overwrite).
 */

export interface PrefillEntry {
  key: string;
  default_value: string | null;
  is_secret_hint: boolean;
}

export interface PrefillForm {
  env_text: string;
  env_secrets_text: string;
  existing_secret_keys: string[];
}

/**
 * Collect the KEY of each non-empty line. `KEY=value` lines yield the part
 * before the first `=`; a bare-name line yields the whole line.
 */
export function parseEnvLineKeys(text: string): string[] {
  const keys: string[] = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf("=");
    const key = (idx === -1 ? trimmed : trimmed.slice(0, idx)).trim();
    if (key) keys.push(key);
  }
  return keys;
}

/**
 * Every env key the form already carries — across plain env, pending secret
 * values, and already-stored secret keys. Used to mark a template row as
 * "already set" and to suppress its Apply button.
 */
export function configuredKeys(f: PrefillForm): Set<string> {
  const keys = new Set<string>();
  for (const k of parseEnvLineKeys(f.env_text)) keys.add(k);
  for (const k of parseEnvLineKeys(f.env_secrets_text)) keys.add(k);
  for (const k of f.existing_secret_keys) keys.add(k);
  return keys;
}

/**
 * Apply one template entry to the form. Returns the same object reference when
 * the key is already configured (so React can bail out of the update).
 */
export function applyPrefill<T extends PrefillForm>(prev: T, entry: PrefillEntry): T {
  if (configuredKeys(prev).has(entry.key)) return prev;
  if (entry.is_secret_hint) {
    const line = `${entry.key}=`;
    return {
      ...prev,
      env_secrets_text: prev.env_secrets_text ? `${prev.env_secrets_text}\n${line}` : line,
    };
  }
  const line = `${entry.key}=${entry.default_value ?? ""}`;
  return {
    ...prev,
    env_text: prev.env_text ? `${prev.env_text}\n${line}` : line,
  };
}

/** Apply every entry in order, skipping already-configured keys. */
export function applyPrefillAll<T extends PrefillForm>(prev: T, entries: PrefillEntry[]): T {
  return entries.reduce((acc, e) => applyPrefill(acc, e), prev);
}
